import React, { useEffect, useState, useContext } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import brLocale from 'date-fns/locale/pt-BR';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Button, Grid, TextField, ButtonGroup } from '@material-ui/core';
import Typography from "@material-ui/core/Typography";
import api from '../../services/api';
import { format, differenceInCalendarDays } from 'date-fns';
import { toast } from 'react-toastify';
import './button.css';
import { i18n } from '../../translate/i18n';
import { AuthContext } from "../../context/Auth/AuthContext";
import { useTheme } from '@material-ui/core';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

export const options = {
    responsive: true,
    scales: {
        x: {
            stacked: true,
        },
        y: {
            stacked: true,
        },
    },
    plugins: {
        legend: {
            position: 'top',
            display: true,
        },
        title: {
            display: true,
            text: 'Tickets',
            position: 'left',
        },
        datalabels: {
            display: true,
            anchor: 'center',
            align: 'center',
            color: "#fff",
            textStrokeColor: "#000",
            textStrokeWidth: 2,
            font: {
                size: 14,
                weight: "bold",
            },
            formatter: (value) => {
                return value > 0 ? value : '';
            },
        }
    },
};

export const ChartsDate = () => {
    const theme = useTheme();
    const [initialDate, setInitialDate] = useState(new Date());
    const [finalDate, setFinalDate] = useState(new Date());
    const [ticketsData, setTicketsData] = useState({ data: [], count: 0 });
    const [viewMode, setViewMode] = useState(null); // null = auto, "day" or "hour"
    const { user } = useContext(AuthContext);

    const companyId = user.companyId;

    const isMultiDay = differenceInCalendarDays(finalDate, initialDate) >= 1;

    useEffect(() => {
        if (companyId) {
            handleGetTicketsInformation();
        }
    }, [companyId]);

    // Reset viewMode when dates change to single day
    useEffect(() => {
        if (!isMultiDay) {
            setViewMode(null);
        }
    }, [isMultiDay]);

    const dataCharts = {
        labels: ticketsData && ticketsData?.data.length > 0 && ticketsData?.data.map((item) => (item.hasOwnProperty('horario') ? `Das ${item.horario}:00 as ${item.horario}:59` : item.data)),
        datasets: [
            {
                label: 'Receptivo',
                data: ticketsData?.data.length > 0 && ticketsData?.data.map((item) => {
                    return Number(item.totalReceptivo) || 0;
                }),
                backgroundColor: theme.palette.primary.main,
            },
            {
                label: 'Ativo',
                data: ticketsData?.data.length > 0 && ticketsData?.data.map((item) => {
                    return Number(item.totalAtivo) || 0;
                }),
                backgroundColor: '#FF6B35',
            },
        ],
    };

    const handleGetTicketsInformation = async () => {
        try {
            let url = `/dashboard/ticketsDay?initialDate=${format(initialDate, 'yyyy-MM-dd')}&finalDate=${format(finalDate, 'yyyy-MM-dd')}&companyId=${companyId}`;
            if (viewMode) {
                url += `&groupBy=${viewMode}`;
            }
            const { data } = await api.get(url);
            setTicketsData(data);
        } catch (error) {
            toast.error('Erro ao buscar informações dos tickets');
        }
    }

    const handleViewModeChange = (mode) => {
        setViewMode(mode);
    };

    // Re-fetch when viewMode changes (only if multi-day)
    useEffect(() => {
        if (companyId && isMultiDay && viewMode) {
            handleGetTicketsInformation();
        }
    }, [viewMode]);

    return (
        <>
            <Typography component="h2" variant="h6" color="primary" gutterBottom>
                {i18n.t("dashboard.users.totalAttendances")} ({ticketsData?.count})
            </Typography>

            <Grid container spacing={2} alignItems="center">
                <Grid item>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                        <DatePicker
                            value={initialDate}
                            onChange={(newValue) => { setInitialDate(newValue) }}
                            label={i18n.t("dashboard.date.initialDate")}
                            renderInput={(params) => <TextField fullWidth {...params} sx={{ width: '20ch' }} />}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                        <DatePicker
                            value={finalDate}
                            onChange={(newValue) => { setFinalDate(newValue) }}
                            label={i18n.t("dashboard.date.finalDate")}
                            renderInput={(params) => <TextField fullWidth {...params} sx={{ width: '20ch' }} />}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item>
                    <Button style={{ backgroundColor: theme.palette.primary.main, top: '10px' }} onClick={handleGetTicketsInformation} variant='contained'>Filtrar</Button>
                </Grid>
                {isMultiDay && (
                    <Grid item>
                        <ButtonGroup size="small" style={{ top: '5px' }}>
                            <Button
                                variant={(!viewMode || viewMode === 'day') ? 'contained' : 'outlined'}
                                style={(!viewMode || viewMode === 'day')
                                    ? { backgroundColor: theme.palette.primary.main, color: '#fff' }
                                    : {}
                                }
                                onClick={() => handleViewModeChange('day')}
                            >
                                Por Dia
                            </Button>
                            <Button
                                variant={viewMode === 'hour' ? 'contained' : 'outlined'}
                                style={viewMode === 'hour'
                                    ? { backgroundColor: theme.palette.primary.main, color: '#fff' }
                                    : {}
                                }
                                onClick={() => handleViewModeChange('hour')}
                            >
                                Por Hora
                            </Button>
                        </ButtonGroup>
                    </Grid>
                )}
            </Grid>
            <Bar options={options} data={dataCharts} style={{ maxWidth: '100%', maxHeight: '280px', }} />
        </>
    );
}